// SEO and LLMO utility functions for TanStack Start
// Based on: https://tanstack.com/start/latest/docs/framework/react/guide/seo
// LLMO: https://tanstack.com/start/latest/docs/framework/react/guide/llmo

export interface OrganizationSchema {
  name: string
  url: string
  logo: string
  description: string
  sameAs?: string[]
  contactPoint?: {
    type: string
    telephone?: string
    email?: string
    contactType: string
  }
}

export interface ArticleSchema {
  headline: string
  description: string
  image: string
  author: {
    name: string
    url?: string
  }
  datePublished: string
  dateModified?: string
  publisher: OrganizationSchema
}

export interface ProductSchema {
  name: string
  description: string
  image?: string
  offers: {
    price: string
    priceCurrency: string
    availability?: string
  }[]
}

export interface WebPageSchema {
  name: string
  description: string
  url: string
}

export interface BreadcrumbSchema {
  itemListElement: {
    position: number
    name: string
    item: string
  }[]
}

// Organization JSON-LD for Zapify
export function createOrganizationSchema(custom?: Partial<OrganizationSchema>) {
  const schema = {
    name: custom?.name || 'Zapify Pro',
    url: custom?.url || 'https://zapify.pro',
    description: custom?.description || 'AI-Powered Instagram Comment Management & Automation',
    logo: {
      '@type': 'ImageObject',
      url: custom?.logo || 'https://zapify.pro/logo-small.png',
    },
    sameAs: custom?.sameAs || [
      'https://www.instagram.com/Zapify.Pro',
      'https://www.tiktok.com/@zapifypro',
      'https://www.youtube.com/@ZapifyPro',
      'https://www.facebook.com/ZapifyPro',
      'https://www.threads.com/@zapifypro',
      'https://x.com/ZapifyPro',
      'https://www.linkedin.com/in/zapifypro',
      'https://www.reddit.com/r/ZapifyPro/',
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      email: custom?.contactPoint?.email || '[email protected]',
      contactType: custom?.contactPoint?.contactType || 'Customer Support',
    },
  }

  return {
    type: 'application/ld+json' as const,
    children: JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Organization',
      ...schema,
    }),
  }
}

// Article JSON-LD for blog posts
export function createArticleSchema(article: ArticleSchema) {
  return {
    type: 'application/ld+json' as const,
    children: JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Article',
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `https://zapify.pro/article/${encodeURIComponent(article.headline)}`,
      },
      headline: article.headline,
      description: article.description,
      image: article.image,
      author: {
        '@type': 'Person',
        name: article.author.name,
        url: article.author.url,
      },
      publisher: {
        '@type': 'Organization',
        name: article.publisher.name,
        logo: {
          '@type': 'ImageObject',
          url: article.publisher.logo,
        },
      },
      datePublished: article.datePublished,
      dateModified: article.dateModified || article.datePublished,
    }),
  }
}

// WebPage JSON-LD for general pages
export function createWebPageSchema(page: WebPageSchema) {
  return {
    type: 'application/ld+json' as const,
    children: JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: page.name,
      description: page.description,
      url: page.url,
    }),
  }
}

// Breadcrumb JSON-LD for navigation
export function createBreadcrumbSchema(breadcrumb: BreadcrumbSchema) {
  return {
    type: 'application/ld+json' as const,
    children: JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumb.itemListElement.map((item) => ({
        '@type': 'ListItem',
        position: item.position,
        name: item.name,
        item: item.item,
      })),
    }),
  }
}

// Product JSON-LD for pricing pages
export function createProductSchema(product: ProductSchema) {
  return {
    type: 'application/ld+json' as const,
    children: JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: product.name,
      description: product.description,
      image: product.image,
      offers: product.offers.map((offer) => ({
        '@type': 'Offer',
        price: offer.price,
        priceCurrency: offer.priceCurrency,
        availability: offer.availability || 'https://schema.org/InStock',
      })),
    }),
  }
}

// SoftwareApplication JSON-LD for SaaS products
export function createSoftwareApplicationSchema() {
  return {
    type: 'application/ld+json' as const,
    children: JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'Zapify Pro',
      description: 'AI-Powered Instagram Comment Management & Automation. Reply to every Instagram comment in your own voice using AI.',
      // Remove AI Replies Branding from SEO
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'All',
      url: 'https://zapify.pro',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: 4.8,
        bestRating: 5,
        ratingCount: 150,
      },
    }),
  }
}

// Free Tool JSON-LD (SoftwareApplication for individual tools)
export function createToolSchema(tool: {
  name: string
  description: string
  url: string
}) {
  return {
    type: 'application/ld+json' as const,
    children: JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: tool.name,
      description: tool.description,
      url: tool.url,
      applicationCategory: 'UtilitiesApplication',
      operatingSystem: 'Web',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
      },
      author: {
        '@type': 'Organization',
        name: 'Zapify Pro',
        url: 'https://zapify.pro',
      },
    }),
  }
}

// HowTo JSON-LD for step-by-step guides
export function createHowToSchema(howTo: {
  name: string
  description: string
  steps: { name: string; text: string }[]
}) {
  return {
    type: 'application/ld+json' as const,
    children: JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name: howTo.name,
      description: howTo.description,
      step: howTo.steps.map((step, i) => ({
        '@type': 'HowToStep',
        position: i + 1,
        name: step.name,
        text: step.text,
      })),
    }),
  }
}

// FAQPage JSON-LD for FAQ sections
export function createFAQSchema(faqs: { question: string; answer: string }[]) {
  return {
    type: 'application/ld+json' as const,
    children: JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    }),
  }
}

// CollectionPage JSON-LD for tool directory
export function createCollectionSchema(collection: {
  name: string
  description: string
  url: string
  items: { name: string; url: string }[]
}) {
  return {
    type: 'application/ld+json' as const,
    children: JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: collection.name,
      description: collection.description,
      url: collection.url,
      mainEntity: {
        '@type': 'ItemList',
        numberOfItems: collection.items.length,
        itemListElement: collection.items.map((item, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: item.name,
          url: item.url,
        })),
      },
    }),
  }
}

// LLMO meta tags helper
export function createLLMOMeta(data: {
  title: string
  description: string
  author?: string
  publishedTime?: string
  modifiedTime?: string
  url: string
  image?: string
  ogType?: string
  noIndex?: boolean
}) {
  const ogImage = data.image || 'https://cms.zapify.pro/uploads/OG_image1_f82f5871e2.png'

  const meta = [
    { title: data.title },
    { name: 'description', content: data.description },
    { property: 'og:title', content: data.title },
    { property: 'og:description', content: data.description },
    { property: 'og:url', content: data.url },
    { property: 'og:site_name', content: 'Zapify Pro' },
    { property: 'og:type', content: data.ogType || 'website' },
    { name: 'robots', content: data.noIndex ? 'noindex, nofollow' : 'index, follow' },
    // Twitter Card
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:site', content: '@ZapifyPro' },
    { name: 'twitter:url', content: data.url },
    { name: 'twitter:title', content: data.title },
    { name: 'twitter:description', content: data.description },
    { name: 'twitter:image', content: ogImage },
  ]

  if (data.author) {
    meta.push({ name: 'author', content: data.author })
  }

  if (data.publishedTime) {
    meta.push({ property: 'article:published_time', content: data.publishedTime })
  }

  if (data.modifiedTime) {
    meta.push({ property: 'article:modified_time', content: data.modifiedTime })
  }

  return meta
}
